import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Dialog from '@material-ui/core/Dialog'
import { useSelector, useDispatch } from 'react-redux'

import styles from './styles.module.scss'

import { getPlaceTargets, updatePlaceTargets, getPlaceEmployees, getPlaceEmployeeTargets, updatePlaceEmployeeTargets } from '../../../../api'
import {
  addSnackbar,
  dismissSnackbar,
} from '../../../../store/organizationList/actions';

import { CrossIcon } from '../icons'
import AppNavbar from '../../../Core/AppNavbar'
import TargetsForm from '../TargetsForm'
import EmployeeTargets from '../EmployeeTargets'

const EMPTY_EMPLOYEE_TARGETS = {
  active: true,
  sales_target: false,
  sales_target_type: 'daily',
  sales_target_amount: '0',
  sales_target_monthly_amount: '0',
  bonus: false,
  bonus_type: 'fixed',
  bonus_amount: '0',
}

const SettingsModal = forwardRef(({companyId, placeId}, ref) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [open, setOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('targets')
  const [employees, setEmployees] = useState([])
  const [placeTagets, setPlaceTargets] = useState({
    target_type: 'monthly',
    amount: 0,
    material_cost: 0,
    employee_cost: 0,
  })

  useEffect(() => {
    if (open && companyId && placeId) {
      getPlaceTargets(companyId, placeId).then(res => {
        setPlaceTargets(res)
      })
      getEmployeeTargets()
    }
  }, [open, companyId, placeId])

  const options = [{
    title: t('Targets'),
    key: 'targets',
    badge: 0,
  }, {
    title: t('Employee targets'),
    key: 'employeeTargets',
    badge: 0,
  }]

  useImperativeHandle(ref, () => ({
    open: () => {
      setOpen(true)
    },
    close: () => {
    }
  }))

  const getEmployeeTargets = async () => {
    const [targets, employees] = await Promise.all([
      getPlaceEmployeeTargets(companyId, placeId),
      getPlaceEmployees(companyId, placeId)
    ])
    if (Array.isArray(targets)) {
      const targetsMap = targets.reduce((acc, target) => {
        return {
          ...acc,
          [target.employee_id]: {
            active: target.active,
            sales_target: target.sales_target,
            sales_target_type: target.sales_target_type,
            sales_target_amount: target.sales_target_amount,
            sales_target_monthly_amount: target.sales_target_monthly_amount,
            bonus: target.bonus,
            bonus_type: target.bonus_type,
            bonus_amount: target.bonus_amount,
          }
        }
      }, {})
      if (Array.isArray(employees)) {
        setEmployees(employees.map(emp => ({
          ...emp,
          title: `${emp.name} ${emp.surname}`,
          checked: Boolean(targetsMap[emp.id]),
          isEmployee: true,
          targets: targetsMap[emp.id] || EMPTY_EMPLOYEE_TARGETS,
        })))
      }
    }
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleSelectTab = key => {
    setActiveTab(key)
  }

  const handleTargetsSubmit = () => {
    updatePlaceTargets(companyId, placeId, placeTagets).then(res => {
      if (res) {
        dispatch(addSnackbar(t('Targets updated successfully'), 'success'))
        setTimeout(() => {
          dispatch(dismissSnackbar())
        }, 2000)
      }
    })
  }

  const handleSubmitEmployeeTargets = () => {
    const post = {
      employee_targets: employees.filter(emp => emp.checked).map(emp => {
        return {
          employee_id: emp.id,
          ...emp.targets,
        }
      })
    }
    updatePlaceEmployeeTargets(companyId, placeId, post).then(res => {
      if (res) {
        dispatch(addSnackbar(t('Employee targets updated successfully'), 'success'))
        setTimeout(() => {
          dispatch(dismissSnackbar())
        }, 2000)
      }
    })
  }

  return (
    <Dialog onClose={() => handleClose(false)} maxWidth={'md'} open={open}>
      <div className={styles.container}>
        <div className={styles.header}>
          { t('Settings') }
          <div className={styles.closeButton} onClick={handleClose}>
            <CrossIcon />
          </div>
        </div>
        <AppNavbar
          options={options}
          selected={activeTab}
          className={styles.navbar}
          highlighted={false}
          onSelect={handleSelectTab} />
        {
          (tab => {
            switch (tab) {
              case 'targets':
                return (
                  <TargetsForm
                    targets={placeTagets}
                    onChange={(field, value) => setPlaceTargets(prev => ({ ...prev, [field]: value }))}
                    onSubmit={handleTargetsSubmit} />
                )
              case 'employeeTargets':
                return (
                  <EmployeeTargets
                    onChangeEmployees={(updatedEmployees) => setEmployees(updatedEmployees)}
                    employees={employees}
                    onSubmit={handleSubmitEmployeeTargets} />
                )
            }
          })(activeTab)
        }
      </div>
    </Dialog>
  )
})

export default SettingsModal
